// Simple Questions API Service
// This replaces mock data with real API calls

const API_BASE_URL = '/api/questions'; // Adjust this to your actual API endpoint

class SimpleQuestionsApi {
  // Get all questions
  async getQuestions(filters = {}) {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.module_type) queryParams.append('module_type', filters.module_type);
      if (filters.module_reference_id) queryParams.append('module_reference_id', filters.module_reference_id);
      if (filters.project_id) queryParams.append('project_id', filters.project_id);
      if (filters.company_id) queryParams.append('company_id', filters.company_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.priority) queryParams.append('priority', filters.priority);
      if (filters.type) queryParams.append('type', filters.type);
      
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/list${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        return { success: true, data: data.data || [] };
      } else {
        throw new Error(data.message || 'API returned error');
      }
    } catch (error) {
      console.warn('API not available, using mock data:', error.message);
      
      // Use mock data with filtering
      let filteredQuestions = this.getMockQuestions();
      
      // Apply filters
      if (filters.module_type) {
        filteredQuestions = filteredQuestions.filter(q => q.module_type === filters.module_type);
      }
      if (filters.module_reference_id) {
        filteredQuestions = filteredQuestions.filter(q => q.module_reference_id === filters.module_reference_id);
      }
      if (filters.project_id) {
        filteredQuestions = filteredQuestions.filter(q => q.project_id === filters.project_id);
      }
      if (filters.company_id) {
        filteredQuestions = filteredQuestions.filter(q => q.company_id === filters.company_id);
      }
      if (filters.status) {
        filteredQuestions = filteredQuestions.filter(q => q.status === filters.status);
      }
      if (filters.category) {
        filteredQuestions = filteredQuestions.filter(q => q.category === filters.category);
      }
      
      return { success: true, data: filteredQuestions };
    }
  }

  // Get single question by ID
  async getQuestionById(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/question/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        return { success: true, data: data.data };
      } else {
        throw new Error(data.message || 'Question not found');
      }
    } catch (error) {
      console.warn('API not available, using mock data:', error.message);
      return { success: false, error: error.message };
    }
  }

  // Create new question
  async createQuestion(questionData) {
    try {
      // Determine module type
      const moduleType = questionData.context || questionData.module_type || 'general';
      
      // Transform frontend data to match API expectations
      const apiData = {
        user_id: questionData.user_id || '1', // TODO: Get from auth context
        title: questionData.subject || questionData.title,
        description: questionData.content || questionData.description,
        category: questionData.category || 'general',
        priority: questionData.priority === 'high' ? 'high' : 'medium',
        module_type: moduleType
      };

      // Only include module-specific fields if not general
      if (moduleType !== 'general') {
        apiData.module_reference_id = questionData.module_reference_id;
        apiData.module_reference_name = questionData.reference || questionData.module_reference_name;
        apiData.project_id = questionData.project_id;
        apiData.company_id = questionData.company_id;
      }

      const response = await fetch(`${API_BASE_URL}/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        return { success: true, data: data.data };
      } else {
        throw new Error(data.message || 'Failed to create question');
      }
    } catch (error) {
      console.error('❌ CREATE QUESTION API FAILED:', error);
      console.error('Error details:', error.message, error.stack);
      console.error('Original question data:', questionData);
      
      // DON'T use mock data - return error
      return { 
        success: false, 
        error: `Failed to create question: ${error.message}`,
        message: 'API call failed - check console for details'
      };
    }
  }

  // Add response to question
  async addResponse(questionId, responseData) {
    try {
      const apiData = {
        user_id: responseData.user_id || '1',
        user_name: responseData.user_name || 'Unknown',
        response_text: responseData.response_text,
        is_from_admin: responseData.is_from_admin || false,
        parent_response_id: responseData.parent_response_id
      };

      const response = await fetch(`${API_BASE_URL}/${questionId}/responses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        return { success: true, data: data.data };
      } else {
        throw new Error(data.message || 'Failed to add response');
      }
    } catch (error) {
      console.warn('API not available, using mock data:', error.message);
      return { success: false, error: error.message };
    }
  }

  // Update question status
  async updateQuestionStatus(questionId, status) {
    try {
      const response = await fetch(`${API_BASE_URL}/question/${questionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        return { success: true, data: data.data };
      } else {
        throw new Error(data.message || 'Failed to update status');
      }
    } catch (error) {
      console.warn('API not available, using mock data:', error.message);
      return { success: false, error: error.message };
    }
  }

  // Bulk import questions
  async bulkImportQuestions(data) {
    try {
      const { questions, mode, user_id } = data;
      
      const response = await fetch(`${API_BASE_URL}/bulk-import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          questions,
          mode,
          user_id
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'success') {
        return { success: true, ...result };
      } else {
        throw new Error(result.message || 'Bulk import failed');
      }
    } catch (error) {
      console.warn('API not available, using mock bulk import:', error.message);
      
      // Mock bulk import for demo
      const mockResult = {
        success: true,
        imported_count: data.questions.filter(q => q.title && q.category).length,
        skipped_count: data.questions.length - data.questions.filter(q => q.title && q.category).length,
        errors: []
      };
      
      return mockResult;
    }
  }

  // Fallback to empty data if API is not available
  getMockQuestions() {
    // Return empty array - no mock/dummy questions
    return [];
  }
}

// Create singleton instance
const simpleQuestionsApi = new SimpleQuestionsApi();

export default simpleQuestionsApi;
